import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { canvasToCameraJpeg } from "../../utils/facialImage";

export default function StaffFaceCaptureButton({
  adminId,
  name,
}: {
  adminId: string;
  name: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [open, setOpen] = useState(false),
    [saving, setSaving] = useState(false),
    [status, setStatus] = useState<"none" | "pending" | "synced" | "error">(
      "none",
    ),
    [error, setError] = useState("");
  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from("usuarios_admin_biometria_facial")
        .select("id")
        .eq("admin_id", adminId)
        .maybeSingle();
      if (!data) {
        setStatus("none");
        return;
      }
      const { data: tasks } = await supabase
        .from("control_acceso_sincronizaciones")
        .select("estado")
        .eq("admin_id", adminId);
      const states = (tasks || []).map((t) => t.estado);
      setStatus(
        states.includes("error")
          ? "error"
          : states.length > 0 && states.every((s) => s === "sincronizado")
            ? "synced"
            : "pending",
      );
    })();
  }, [adminId]);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 720 },
          height: { ideal: 960 },
        },
        audio: false,
      })
      .then((stream) => {
        if (!alive) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
      })
      .catch(() =>
        setError(
          "No se pudo abrir la cámara. Revisa los permisos del navegador.",
        ),
      );
    return () => {
      alive = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open]);
  const close = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setOpen(false);
    setError("");
  };
  const capture = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setSaving(true);
    setError("");
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 480;
      canvas.height = 640;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("No se pudo preparar la captura");
      const sourceRatio = video.videoWidth / video.videoHeight,
        targetRatio = canvas.width / canvas.height;
      let sx = 0,
        sy = 0,
        sw = video.videoWidth,
        sh = video.videoHeight;
      if (sourceRatio > targetRatio) {
        sw = video.videoHeight * targetRatio;
        sx = (video.videoWidth - sw) / 2;
      } else {
        sh = video.videoWidth / targetRatio;
        sy = (video.videoHeight - sh) / 2;
      }
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      const image = canvasToCameraJpeg(canvas);
      const { data, error: invokeError } = await supabase.functions.invoke(
        "admin-facial-enroll",
        { body: { admin_id: adminId, image_data_url: image } },
      );
      if (invokeError || !data?.ok)
        throw new Error(
          data?.details ||
            data?.error ||
            invokeError?.message ||
            "No se pudo enrolar",
        );
      setStatus("pending");
      close();
      alert(
        `Rostro guardado. Sincronización enviada a ${data.sync?.total_camaras || 0} cámara(s).`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`px-3 py-2 rounded-lg border text-sm font-medium ${status === "synced" ? "border-green-200 bg-green-50 text-green-700" : status === "error" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}
        title="Capturar rostro"
      >
        <i className="ri-camera-line mr-1" />
        {status === "none"
          ? "Enrolar rostro"
          : status === "synced"
            ? "Rostro sincronizado"
            : status === "error"
              ? "Reintentar rostro"
              : "Rostro pendiente"}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-4 flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-gray-900">
                  Biometría de {name}
                </h3>
                <p className="text-xs text-gray-500">
                  Ubica el rostro dentro del óvalo
                </p>
              </div>
              <button onClick={close} className="p-2">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="relative bg-black aspect-[3/4]">
              <video
                ref={videoRef}
                playsInline
                muted
                className="w-full h-full object-cover -scale-x-100"
              />
              <div className="absolute inset-[12%_18%_18%] rounded-[50%] border-4 border-white shadow-[0_0_0_999px_rgba(0,0,0,.28)]" />
              <div className="absolute bottom-4 inset-x-4 text-center text-white text-xs bg-black/50 rounded-lg p-2">
                Sin lentes oscuros · rostro de frente · buena iluminación
              </div>
            </div>
            {error && (
              <p className="m-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </p>
            )}
            <div className="p-4 flex gap-3">
              <button
                onClick={close}
                className="flex-1 px-4 py-3 border rounded-lg"
              >
                Cancelar
              </button>
              <button
                disabled={saving || !!error}
                onClick={() => void capture()}
                className="flex-1 px-4 py-3 bg-teal-700 text-white rounded-lg disabled:opacity-50"
              >
                <i className="ri-camera-fill mr-2" />
                {saving ? "Guardando…" : "Capturar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
